import { useState, useEffect, useRef, forwardRef, type ReactNode } from 'react'
import {
  View,
  Platform,
  TextInput as RNTextInput,
  type TextInputProps as RNTextInputProps,
} from 'react-native'
import { Pressable } from './Pressable'
import { tintIconNode } from './tint-icon-node'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  interpolateColor,
  FadeInDown,
  FadeOut,
} from 'react-native-reanimated'
import { Eye, EyeOff } from '@/components/ui/icons'
import { selection } from '@/lib/utils/haptics'
import { useTranslation } from 'react-i18next'
import { COLORS, FONTS } from '../../lib/brand'
import {
  useSemanticColors,
  RADIUS,
  SPACING,
  FORM_FIELD_GAP,
  DURATION,
  INTERACTION,
} from '../../lib/theme/tokens'
import { useShakeAnimation } from '../../lib/hooks/use-shake-animation'
import { useTheme } from '../../lib/hooks/use-theme'
import { useFontScale } from '../../lib/hooks/use-font-scale'

interface TextInputProps extends RNTextInputProps {
  label?: string
  error?: string
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  focusColor?: string
}

export const TextInput = forwardRef<RNTextInput, TextInputProps & { className?: string }>(
  function TextInput(
    { label, error, leftIcon, rightIcon, secureTextEntry, className, focusColor, style, ...props },
    ref,
  ) {
    const { t } = useTranslation()
    const { isDark } = useTheme()
    const colors = useSemanticColors(isDark)
    // The field's floor has to move with the text it holds. `fieldHeight` is
    // 54 raw dp; the input inside it is 15pt with SPACING.md of padding above
    // and below, and at 2.0 that line box alone is taller than 54 — so the
    // minimum that was a comfortable floor at 1.0 became a ceiling.
    const { box } = useFontScale()
    const [focused, setFocused] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const prevError = useRef<string | undefined>(undefined)

    const isSecure = secureTextEntry && !showPassword

    // Theme-aware colors
    const borderDefault = colors.border
    const borderFocused = focusColor ?? (isDark ? COLORS.coral.DEFAULT : COLORS.purple.light)
    const eyeColor = colors.textSecondary
    // A placeholder has to read as *absent* text. On `textSecondary` it landed
    // at the same darkness as the field labels above it (#5C5C70 vs #514D59)
    // while being a point larger, so the dispute-form textarea looked like a
    // paragraph the user had already written. `textMuted` is the next step
    // down the ramp and still clears AA on the field fill (4.76:1).
    const placeholderColor = colors.textMuted
    const inputTextColor = colors.textPrimary
    // Inputs are intentionally denser than cards so their editable boundary
    // stays unmistakable, but they should still belong to the same material
    // field as the surrounding screen instead of arriving as opaque grey
    // rectangles over it.
    const bgColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.58)'
    const resolvedLeftIcon = leftIcon ? tintIconNode(leftIcon, colors.textSecondary) : null
    const resolvedRightIcon = rightIcon ? tintIconNode(rightIcon, colors.textSecondary) : null

    // Animated focus border + glow
    const focusProgress = useSharedValue(0)

    useEffect(() => {
      focusProgress.value = withTiming(focused ? 1 : 0, { duration: DURATION.fast })
    }, [focused, focusProgress])

    const isWeb = Platform.OS === 'web'
    const containerAnimStyle = useAnimatedStyle(() => {
      const glowColor = error ? COLORS.coral.DEFAULT : borderFocused
      const bc = error
        ? COLORS.coral.DEFAULT
        : interpolateColor(focusProgress.value, [0, 1], [borderDefault, borderFocused])

      if (isWeb) {
        const opacity = focusProgress.value * 0.12
        const radius = focusProgress.value * 10
        const glowRgb = glowColor === COLORS.purple.light ? '91,84,144' : '239,78,74'
        return {
          borderColor: bc,
          boxShadow: `0px 0px ${radius}px 0px rgba(${glowRgb},${opacity})`,
        }
      }
      return {
        borderColor: bc,
        shadowColor: glowColor,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: focusProgress.value * 0.12,
        shadowRadius: focusProgress.value * 10,
      }
    })

    // Label opacity on focus
    const labelOpacity = useAnimatedStyle(() => ({
      opacity: focusProgress.value * 0.25 + 0.75,
    }))

    // Error shake
    const { animatedStyle: shakeStyle, shake } = useShakeAnimation()

    useEffect(() => {
      if (error && !prevError.current) {
        shake()
      }
      prevError.current = error
    }, [error, shake])

    const togglePassword = () => {
      setShowPassword((prev) => !prev)
      void selection()
    }

    return (
      // Bottom margin sourced from FORM_FIELD_GAP (single token in tokens.ts)
      // instead of the prior NativeWind `mb-7`. The className+Animated.View
      // combo was occasionally dropping the margin on web, leading to the
      // visually-uneven form rhythm Carlos called out. Same token used by
      // PhoneInput / DatePicker / Select.
      //
      // That last sentence used to end "so all four match exactly", which was
      // not true and had not been measured: DatePicker set its icon gap to
      // `SPACING.sm + 2` and its height to a literal 54, so the date row's text
      // started 2px left of every other row in the same form. A comment
      // asserting four components agree is worth nothing unless one number
      // makes them agree — that number is now INTERACTION.fieldIconGap.
      <Animated.View style={[shakeStyle, { marginBottom: FORM_FIELD_GAP }]} className={className}>
        {label ? (
          <Animated.Text
            style={[
              labelOpacity,
              {
                fontFamily: FONTS.body,

                fontSize: 14,
                letterSpacing: 0.2,
                // Was SPACING.sm + 2 (10px). The label sat almost on top of
                // the input border on web — bumped to SPACING.md (16px) to
                // give every form row consistent breathing room.
                marginBottom: SPACING.md,
                color: colors.textPrimary,
              },
            ]}
          >
            {label}
          </Animated.Text>
        ) : null}
        <Animated.View
          style={[
            containerAnimStyle,
            {
              backgroundColor: bgColor,
              flexDirection: 'row',
              alignItems: 'center',
              borderRadius: RADIUS.md,
              borderWidth: 1,
              paddingHorizontal: SPACING.md,
              // The vertical padding lives on the input itself, not here.
              //
              // With it on this wrapper and `alignItems: 'center'`, the input
              // was sized to its own line box and centred: a 54px-tall field
              // whose focusable area was the middle 19px. On web that is not
              // cosmetic — a View is not a <label>, so clicking the other 35px
              // did nothing at all. Measured on the DUI form: 258x19.
              paddingVertical: 0,
              minHeight: box(INTERACTION.fieldHeight),
            },
          ]}
        >
          {resolvedLeftIcon ? (
            <View style={{ marginRight: INTERACTION.fieldIconGap }}>{resolvedLeftIcon}</View>
          ) : null}
          {/* Use the platform TextInput directly so native keyboard composition and
            autocorrect keep ownership of the text buffer. */}
          {/* `style` is pulled out of `props` on purpose. It used to ride along
              in the `{...props}` spread below, which lands AFTER this prop and
              therefore REPLACED the whole style object instead of merging with
              it. Any caller passing a style — `style={{ minHeight: 100 }}` on
              the multiline description field of the listing wizard — silently
              lost `flex: 1`, the text colour, the font and the vertical
              padding. Without `flex: 1` a multiline field falls back to the
              intrinsic width of a `<textarea>` (~20 characters), which is why
              that placeholder wrapped into three ragged lines inside a box
              three times wider. Merging keeps the defaults and lets the caller
              override single keys. */}
          {Platform.OS === 'web' ? (
            <RNTextInput
              ref={ref}
              style={[
                {
                  flex: 1,
                  // A flex child on web keeps `min-width: auto` for replaced
                  // elements, so the intrinsic textarea width can still win.
                  minWidth: 0,
                  alignSelf: 'stretch',
                  color: inputTextColor,
                  fontFamily: FONTS.body,
                  fontSize: 15,
                  paddingVertical: SPACING.md,
                },
                { outlineStyle: 'none' } as Record<string, string>,
                style,
              ]}
              placeholderTextColor={placeholderColor}
              selectionColor={COLORS.coral.DEFAULT}
              secureTextEntry={isSecure}
              accessibilityLabel={label}
              onFocus={(e) => {
                setFocused(true)
                props.onFocus?.(e)
              }}
              onBlur={(e) => {
                setFocused(false)
                props.onBlur?.(e)
              }}
              {...props}
            />
          ) : (
            <RNTextInput
              ref={ref}
              style={[
                {
                  flex: 1,
                  minWidth: 0,
                  alignSelf: 'stretch',
                  color: inputTextColor,
                  fontFamily: FONTS.body,
                  fontSize: 15,
                  paddingVertical: SPACING.md,
                },
                style,
              ]}
              placeholderTextColor={placeholderColor}
              selectionColor={COLORS.coral.DEFAULT}
              secureTextEntry={isSecure}
              accessibilityLabel={label}
              onFocus={(e) => {
                setFocused(true)
                props.onFocus?.(e)
              }}
              onBlur={(e) => {
                setFocused(false)
                props.onBlur?.(e)
              }}
              {...props}
            />
          )}
          {secureTextEntry && (
            <Pressable
              onPress={togglePassword}
              accessibilityLabel={showPassword ? t('a11y.hide_password') : t('a11y.show_password')}
              accessibilityRole="button"
              hitSlop={8}
              style={{
                marginLeft: SPACING.sm,
                // hitSlop is dropped by react-native-web, so the box has to be
                // the target: this was a 28pt control.
                minWidth: INTERACTION.minTouchTarget,
                minHeight: INTERACTION.minTouchTarget,
                alignItems: 'center',
                justifyContent: 'center',
                padding: SPACING.xs,
                ...(Platform.OS === 'web' ? { cursor: 'pointer' as unknown as undefined } : {}),
              }}
            >
              {showPassword ? (
                <EyeOff size={20} color={eyeColor} />
              ) : (
                <Eye size={20} color={eyeColor} />
              )}
            </Pressable>
          )}
          {resolvedRightIcon && !secureTextEntry ? (
            <View style={{ marginLeft: SPACING.sm }}>{resolvedRightIcon}</View>
          ) : null}
        </Animated.View>
        {error ? (
          <Animated.Text
            entering={FadeInDown.duration(DURATION.fast)}
            exiting={FadeOut.duration(DURATION.fast)}
            accessibilityRole="alert"
            accessibilityLiveRegion="assertive"
            style={{
              color: colors.textAccent,
              fontFamily: FONTS.body,

              fontSize: 12,
              marginTop: SPACING.xs,
            }}
          >
            {error}
          </Animated.Text>
        ) : null}
      </Animated.View>
    )
  },
)
